import React from 'react'

import HeaderMeta from 'components/HeaderMeta'

import Content, { Theme as ContentTheme } from 'components/base/Content'
import Paragraph, { Theme as ParagraphTheme } from 'components/base/Paragraph'
import Title from 'components/base/Title'
import Link from 'components/base/Link'
import InsuranceTypeIcon, { Theme as InsuranceTypeIconTheme } from 'components/base/Icon/InsuranceTypeIcon'
import Img from 'components/base/Img'
import IconGrid from 'components/base/Icon/Grid'
import { Theme as IconTheme } from 'components/base/Icon/Grid/Icon'

import PageHero, { Theme as PageHeroTheme } from 'components/blocks/PageHero'
import AskQuestion from 'components/blocks/AskQuestion'

import HowItWorks from 'assets/images/products/HowItWorks.svg'
import DisabilityStretching from 'assets/images/products/DisabilityStretching.svg'

const ProductsDisabilityInsurance: React.FC = (): React.ReactElement=> (
  <div>
    <HeaderMeta
      title='Disability Insurance | Insurance Lounge'
      description='Insurance Lounge offers a variety of disability insurance products with policies to fit each client’s specific situation. Have questions? Contact us today!'
      ogImg='/images/og-images/Disability.webp'
    />
    <PageHero
      cta={true}
      ctaTitle='Get a disability insurance quote now.'
      theme={PageHeroTheme.disability}
      trackingInfo={{ page_category: 'product_page_disability' }}
    >
      Disability Insurance
    </PageHero>
    <Content short={true} margined={true} center={true}>
      <InsuranceTypeIcon theme={InsuranceTypeIconTheme.disability} colored={true} hasMargin={true} />
      <Paragraph theme={ParagraphTheme.introLarge}>
        Disability insurance can offer you up to 80% of your income if you’re unable to work because of an injury or illness.
        Having this security can help cover various expenses during unexpected issues. Insurance Lounge offers a variety of
        disability insurance products with policies that fit each client’s specific situation.
      </Paragraph>
      <Title level={2}>What Is Disability Insurance?</Title>
      <Paragraph>No one ever expects to be involved in an accident, but things happen. That’s why you should have disability
        insurance for income protection in case you can’t go to work. The benefits you receive from disability insurance
        can be used to pay for groceries, bills, or anything else you need. Before you purchase a disability policy, review our
        breakdown of your different coverage options.
      </Paragraph>
    </Content>
    <Content theme={ContentTheme.yellow}>
      <Content theme={ContentTheme.paddedBelow} center={true} short={true}>
        <Img
          src={HowItWorks}
          src2x={undefined}
          style={{
            height: 180,
            width: 'auto',
          }}
          alt='How It Works'
        />
      </Content>
      <Content center={true} short={true}>
        <Title level={2}>Understanding How Disability Insurance Works</Title>
        <Paragraph>
          If you have a disability policy, your insurer will periodically pay out benefits while you’re unable to work.
          The five features included in every policy are:
        </Paragraph>
        <Title level={3} noMargin={true}>Premiums</Title>
        <Paragraph>
          This is how much you pay each month for your policy. The premium will vary based on several factors, including
          your health and the type of coverage you have.
        </Paragraph>
        <Title level={3} noMargin={true}>Benefits</Title>
        <Paragraph>
          This is the amount of money you will receive while you are unable to work. Your benefits should not be taxed and
          typically covers between 60-80% of your usual income.
        </Paragraph>
        <Title level={3} noMargin={true}>Benefit periods</Title>
        <Paragraph>
          This refers to how long you will receive benefits. Your benefit period will vary based on how long it takes to
          recover from your disability.
        </Paragraph>
        <Title level={3} noMargin={true}>Waiting periods</Title>
        <Paragraph>
          This refers to how long you will have to wait before receiving benefits.
        </Paragraph>
        <Title level={3} noMargin={true}>Definition of the disability</Title>
        <Paragraph>
          An own-occupation disability means you qualify if you can’t work in your own occupation. An any-occupation
          disability means you can only qualify for benefits if your ailment prevents you from any form of employment.
        </Paragraph>
      </Content>
    </Content>
    <Content margined={true} center={true} short={true}>
      <Content theme={ContentTheme.paddedBelow} center={true} short={true}>
        <Img
          src={DisabilityStretching}
          src2x={undefined}
          style={{
            height: 280,
            width: 'auto',
          }}
          alt='Disability Stretching'
        />
      </Content>
      <Title level={2}>Factors That Affect Your Insurance Rate</Title>
      <Paragraph>
        Several variables can affect the cost of disability policies. Some of these factors include:
      </Paragraph>
      <IconGrid
        icons={[
          {
            theme: IconTheme.age,
            title: '',
          },
          {
            theme: IconTheme.health,
            title: '',
          },
          {
            theme: IconTheme.occupation,
            title: '',
          },
          {
            theme: IconTheme.location,
            title: '',
          },
          {
            theme: IconTheme.salary,
            title: '',
          },
          {
            theme: IconTheme.period,
            title: '',
          },
        ]}
      />
    </Content>
    <Content theme={ContentTheme.gray}>
      <Content center={true} short={true}>
        <Title level={2}>Common Types of Disability Policies</Title>
        <Paragraph>
          You can choose between disability insurance that is publicly or privately owned based on your position.
          You can also receive Social Security disability insurance if you’ve worked in jobs covered by Social
          Security or have a condition that meets the requirements.
        </Paragraph>
        <Title level={3} noMargin={true}>Long-term Disability Insurance</Title>
        <Paragraph>
          Long-term disability insurance protects against disabilities that affect you for an extensive period.
          It will also cover serious injuries and illnesses that prevent you from returning to working permanently.
        </Paragraph>
        <Title level={3} noMargin={true}>Short-term Disability Insurance</Title>
        <Paragraph>
          Short-term disability policies pay out benefits for less severe impairments that are expected to last for
          less than a year. This coverage provides up to 80% of your pre-tax income.
        </Paragraph>
        <Paragraph>
          Insurance Lounge is committed to providing clients with a positive insurance experience so that policyholders
          understand what benefits they have. Along with disability coverage, we also offer health
          insurance, <Link to='/products/homeowners-insurance'>homeowners insurance</Link>, auto insurance, and more.
          Our insurance concierges can offer assistance in person, online, or over the phone. To get a free
          quote, <Link to='/quote/new'>fill out our form</Link> .
        </Paragraph>
      </Content>
    </Content>
    <AskQuestion title='We’re Here, When You Need Us'>
      Reach out and an Insurance Concierge<sup>®</sup> will get in touch!
    </AskQuestion>
  </div>
)

export default ProductsDisabilityInsurance
